<template>
  <SlideOver
    v-model="open"
    id="client-group-slide-over"
    :title="
      editing
        ? $t('components.client-group-slide-over.edit-client-group')
        : $t('components.client-group-slide-over.new-client-group')
    "
    :closeable="!loading && !isDeleteModalVisible"
    :loading-overlay="loading"
  >
    <Input
      v-model="editClientGroup.name"
      id="name"
      :label="$t('general.name')"
      required
    />

    <Textarea
      v-model="editClientGroup.description"
      id="description"
      :label="$t('general.description')"
    />

    <ColorPicker
      v-model="editClientGroup.color"
      :label="$t('general.choose-color')"
    />

    <template #footer>
      <Button
        v-if="editing"
        type="secondary"
        size="lg"
        icon-only
        :disabled="loading"
        @click="isDeleteModalVisible = true"
      >
        <TrashIcon class="size-4 text-red-500" aria-hidden="true" />
      </Button>
      <Button type="secondary" size="lg" :disabled="loading" @click="cancel">
        {{ $t('general.cancel') }}
      </Button>
      <Button
        size="lg"
        :loading="loading"
        :disabled="!changed"
       
        @click="saveClientGroup"
      >
        {{
          $t(
            'components.client-group-slide-over.' +
              (editing ? 'save-group' : 'create-group')
          )
        }}
      </Button>
    </template>

    <ModalConfirmDelete
      v-model="isDeleteModalVisible"
      :title="$t('inventory.client-groups.delete-client-groups')"
      :text="$t('inventory.client-groups.delete-client-groups-text')"
      :loading="loadingDelete"
      @confirmed="deleteClientGroup"
    />
  </SlideOver>
</template>

<script lang="ts">
export const defaultClientGroup: CreateClientGroup = {
  name: '',
  description: '',
  color: 'indigo',
  rolloutId: '',
};

export type CreateClientGroup = Omit<
  ClientGroup,
  '_id' | 'orgId' | 'created' | 'updated'
>;
</script>

<script lang="ts" setup>
import { isEqual } from 'lodash';
import { klona } from 'klona';
import { TrashIcon } from '@heroicons/vue/20/solid';
import type { ClientGroup, Rollout } from '@/types';
import { useNotifications } from '~~/stores/notifications';
import {
  getGeneralErrorMap,
  getNotificationDataByMap,
  type ErrorMapItem,
} from '~/util/error';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  clientGroup: {
    type: Object as PropType<ClientGroup | CreateClientGroup>,
    default: () => defaultClientGroup,
  },
});

const emit = defineEmits(['update:modelValue', 'saved', 'deleted']);

const i18n = useI18n();

const editClientGroup = ref<ClientGroup | CreateClientGroup>(
  klona(props.clientGroup)
);

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const changed = computed(
  () => !isEqual(props.clientGroup, editClientGroup.value)
);
const editing = computed(() => !isEqual(props.clientGroup, defaultClientGroup));

watch(
  computed(() => props.modelValue),
  (newValue) => {
    if (newValue) {
      editClientGroup.value = klona(props.clientGroup);
      loading.value = false;
      loadingDelete.value = false;
    }
  }
);

const { $client } = useNuxtApp();
const { selectedRollout } = useRolloutRouting();
const notificationsStore = useNotifications();

const isDeleteModalVisible = ref(false);

const loading = ref(false);
const loadingDelete = ref(false);

const cancel = () => {
  if (!loading.value) {
    if (!editing.value) {
      open.value = false;
    } else if (changed.value) {
      editClientGroup.value = klona(props.clientGroup);
    } else {
      open.value = false;
    }
  }
};

const saveClientGroup = async () => {
  if (!editClientGroup.value?.name?.length) {
    notificationsStore.createIncompleteNotification();
    return;
  }

  if (loading.value || !changed.value) {
    return;
  }

  loading.value = true;
  try {
    if (editing.value) {
      await $client.clientGroup.update.mutate({
        ...(editClientGroup.value as ClientGroup),
        rolloutId: (selectedRollout.value as Rollout)._id,
      });
    } else {
      await $client.clientGroup.create.mutate({
        ...editClientGroup.value,
        rolloutId: (selectedRollout.value as Rollout)._id,
      } as CreateClientGroup);
    }
    open.value = false;

    emit('saved');
  } catch (error) {
    loading.value = false;

    const errorMap: ErrorMapItem[] = getGeneralErrorMap(i18n);

    const messageData = getNotificationDataByMap(
      i18n,
      errorMap,
      error as Error
    );

    useErrorHandler(error, {
      type: 'error',
      title: messageData.title,
      message: messageData.message,
    });
  }
};

const deleteClientGroup = async () => {
  if (loadingDelete.value) {
    return;
  }

  loadingDelete.value = true;
  try {
    await $client.clientGroup.delete.mutate({
      ids: [(editClientGroup.value as ClientGroup)._id],
      rolloutId: (selectedRollout.value as Rollout)._id,
    });

    open.value = false;
    isDeleteModalVisible.value = false;

    emit('deleted');
  } catch (error) {
    useErrorHandler(error, {
      type: 'error',
      title: i18n.t('error-messages.something-went-wrong'),
      message: i18n.t('error-messages.please-contact-key-account-manager'),
    });
    loadingDelete.value = false;
  }
};
</script>
